import { useState, useEffect } from "react";
import { useConnections, useDisconnectLinkedIn, connectLinkedIn } from "../../hooks/useConnections";
import type { Platform } from "../../../../shared/types";

interface BrandConnectionsProps {
  brandId: string;
}

const PLATFORMS: { key: Platform; name: string; available: boolean }[] = [
  { key: "linkedin", name: "LinkedIn", available: true },
  { key: "twitter", name: "Twitter / X", available: false },
  { key: "instagram", name: "Instagram", available: false },
  { key: "threads", name: "Threads", available: false },
  { key: "youtube", name: "YouTube", available: false },
  { key: "tiktok", name: "TikTok", available: false },
];

export function BrandConnections({ brandId }: BrandConnectionsProps) {
  const { data, isLoading, refetch } = useConnections(brandId);
  const disconnectLinkedIn = useDisconnectLinkedIn(brandId);
  const connections = data?.data ?? [];
  const [flash, setFlash] = useState<{ type: "success" | "error"; message: string } | null>(null);

  // Handle OAuth return query params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const connection = params.get("connection");
    const status = params.get("status");
    const error = params.get("connection_error");

    if (connection && status === "connected") {
      setFlash({ type: "success", message: `${connection} connected successfully.` });
    } else if (error) {
      const messages: Record<string, string> = {
        user_cancelled_login: "Connection cancelled.",
        user_cancelled_authorize: "Connection cancelled.",
        token_exchange_failed: "Failed to connect. Please try again.",
        profile_fetch_failed: "Failed to fetch profile. Please try again.",
        invalid_state: "Connection expired. Please try again.",
        callback_failed: "Something went wrong. Please try again.",
      };
      setFlash({ type: "error", message: messages[error] || `Connection failed: ${error}` });
    }

    if (connection || error) {
      // Clean up URL
      const url = new URL(window.location.href);
      url.searchParams.delete("connection");
      url.searchParams.delete("status");
      url.searchParams.delete("connection_error");
      window.history.replaceState({}, "", url.toString());

      // Refresh connections list
      refetch();
    }
  }, [refetch]);

  const getConnection = (platform: Platform) =>
    connections.find((c) => c.platform === platform);

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {flash && (
        <div
          className={`flex items-center justify-between rounded-lg px-4 py-3 text-sm ${
            flash.type === "success"
              ? "bg-green-50 text-green-800"
              : "bg-red-50 text-red-800"
          }`}
        >
          <span>{flash.message}</span>
          <button onClick={() => setFlash(null)} className="ml-2 text-current opacity-60 hover:opacity-100">
            &times;
          </button>
        </div>
      )}

      <p className="text-sm text-slate-500">
        Connect your social accounts to publish AI-generated content directly.
      </p>

      {PLATFORMS.map(({ key, name, available }) => {
        const conn = getConnection(key);

        return (
          <div
            key={key}
            className="flex items-center justify-between rounded-lg border border-slate-200 bg-white p-4"
          >
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-100 text-lg font-bold text-slate-600">
                {name[0]}
              </div>
              <div>
                <div className="text-sm font-medium text-slate-900">{name}</div>
                {conn && conn.status === "connected" ? (
                  <div className="flex items-center gap-1.5">
                    <span className="inline-block h-2 w-2 rounded-full bg-green-500" />
                    <span className="text-xs text-slate-500">
                      {conn.platform_username || "Connected"}
                    </span>
                    {conn.token_expiry && (
                      <span className="text-xs text-slate-400">
                        &middot; expires {new Date(conn.token_expiry).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                ) : available ? (
                  <span className="text-xs text-slate-400">Not connected</span>
                ) : (
                  <span className="text-xs text-slate-400">Coming soon</span>
                )}
              </div>
            </div>

            <div>
              {conn && conn.status === "connected" ? (
                <button
                  onClick={() => disconnectLinkedIn.mutate()}
                  disabled={disconnectLinkedIn.isPending}
                  className="rounded-md border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:border-red-300 hover:text-red-600 disabled:opacity-50"
                >
                  {disconnectLinkedIn.isPending ? "..." : "Disconnect"}
                </button>
              ) : available ? (
                <button
                  onClick={() => connectLinkedIn(brandId)}
                  className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-blue-700"
                >
                  Connect
                </button>
              ) : (
                <span className="rounded-md bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-400">
                  Soon
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
